import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import './EndUserPortal.css';

// ── Theme helpers ─────────────────────────────────────────────
/**
 * Convert a 6-digit hex color to { r, g, b }
 */
const hexToRgb = (hex) => {
  const h = hex.replace('#', '');
  if (h.length !== 6) return { r: 124, g: 58, b: 237 };
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  };
};

/** Darken a hex color by mixing it toward black */
const darken = (hex, amount = 30) => {
  const { r, g, b } = hexToRgb(hex);
  const d = (v) => Math.max(0, v - amount).toString(16).padStart(2, '0');
  return `#${d(r)}${d(g)}${d(b)}`;
};

/** Build the set of CSS variable overrides for the portal — set directly, no -val aliases */
const buildAccentVars = (primaryColor) => {
  const { r, g, b } = hexToRgb(primaryColor);
  return {
    '--eu-accent':       primaryColor,
    '--eu-accent-light': `rgba(${r},${g},${b},0.13)`,
    '--eu-accent-glow':  `rgba(${r},${g},${b},0.20)`,
    '--eu-hero-from':    darken(primaryColor, 55),
    '--eu-hero-to':      darken(primaryColor, 30),
    '--eu-card-shadow':  `0 2px 16px rgba(${r},${g},${b},0.14)`,
  };
};

// ── Heroicons (outline) ───────────────────────────────────────
import {
  ExclamationTriangleIcon,
  DocumentTextIcon,
  AcademicCapIcon,
  ShieldCheckIcon,
  BellAlertIcon,
  BookOpenIcon,
  ClipboardDocumentListIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  LightBulbIcon,
  LockClosedIcon,
  EyeSlashIcon,
  WifiIcon,
  PhoneIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

// ── Helpers ──────────────────────────────────────────────────
const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return 'Good Morning';
  if (h < 18) return 'Good Afternoon';
  return 'Good Evening';
};

const formatTime = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return 'N/A'; }
};

// ── Mock / fallback data ──────────────────────────────────────
const MOCK_COURSES = [
  { id: 1, name: 'Security Awareness Basics', progress: 100, emoji: '🛡️', duration: '45 min', status: 'completed' },
  { id: 2, name: 'Phishing & Social Engineering', progress: 65, emoji: '🎣', duration: '30 min', status: 'in_progress' },
  { id: 3, name: 'Password Hygiene & MFA',   progress: 0,  emoji: '🔑', duration: '20 min', status: 'not_started' },
  { id: 4, name: 'Data Handling & GDPR',      progress: 20, emoji: '📋', duration: '35 min', status: 'in_progress' },
];

const MOCK_DOCUMENTS = [
  { id: 1, name: 'Acceptable Use Policy',         status: 'acknowledged', updated: '2026-02-01' },
  { id: 2, name: 'Remote Work Security Guidelines', status: 'pending',      updated: '2026-03-01' },
  { id: 3, name: 'Incident Response Procedures',  status: 'overdue',      updated: '2026-01-15' },
  { id: 4, name: 'BYOD Security Policy',           status: 'pending',      updated: '2026-03-05' },
];

const MOCK_INCIDENTS = [
  { id: 1, title: 'Suspicious email received',  severity: 'high',   status: 'in_progress', date: '2026-03-08' },
  { id: 2, title: 'Laptop left unlocked',        severity: 'low',    status: 'resolved',    date: '2026-03-01' },
  { id: 3, title: 'Unauthorized USB device',     severity: 'medium', status: 'open',        date: '2026-02-20' },
];

const MOCK_ACTIVITY = [
  { id: 1, text: 'Completed "Security Awareness Basics" module', time: '2 hours ago',   color: 'green' },
  { id: 2, text: 'Incident #1 updated to In Progress',            time: 'Yesterday',     color: 'amber' },
  { id: 3, text: 'New document requires acknowledgement',          time: '2 days ago',    color: 'purple' },
  { id: 4, text: 'Reported suspicious email incident',            time: '3 days ago',    color: 'red' },
  { id: 5, text: 'Password reset completed',                      time: '1 week ago',    color: 'blue' },
];

const SECURITY_TIPS = [
  {
    icon: <LockClosedIcon />,
    color: 'var(--eu-accent)',
    bg: 'var(--eu-accent-light)',
    title: 'Use Strong Passwords',
    desc: 'Create unique passwords of at least 12 characters. Use a password manager.',
  },
  {
    icon: <EyeSlashIcon />,
    color: '#3b82f6',
    bg: '#dbeafe',
    title: 'Watch for Phishing',
    desc: "Verify senders before clicking links. When in doubt, don't click — report it.",
  },
  {
    icon: <WifiIcon />,
    color: '#10b981',
    bg: '#d1fae5',
    title: 'Secure Your Network',
    desc: 'Avoid public Wi-Fi for sensitive work. Always use the company VPN remotely.',
  },
  {
    icon: <PhoneIcon />,
    color: '#f59e0b',
    bg: '#fef3c7',
    title: 'Enable MFA Everywhere',
    desc: 'Multi-factor authentication blocks 99% of automated attacks on accounts.',
  },
];

// ── Awareness Score Ring ──────────────────────────────────────
const ScoreRing = ({ score }) => {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const dash = (score / 100) * circ;
  const color = score >= 80 ? '#34d399' : score >= 60 ? '#fbbf24' : '#f87171';

  return (
    <div className="eu-score-ring">
      <svg width="96" height="96" viewBox="0 0 96 96">
        <circle cx="48" cy="48" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="8" />
        <circle
          cx="48" cy="48" r={r}
          fill="none"
          stroke={color}
          strokeWidth="8"
          strokeDasharray={`${dash} ${circ}`}
          strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s cubic-bezier(0.4,0,0.2,1)' }}
        />
      </svg>
      <div className="eu-score-center">
        <span className="eu-score-value">{score}</span>
        <span className="eu-score-label">Score</span>
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────
const EndUserPortal = () => {
  const { user } = useAuth();
  const dashboardData = user?.employee_details?.dashboard || {};
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState(dashboardData.lms|| {});
  const [documents, setDocuments] = useState(dashboardData.documents || MOCK_DOCUMENTS);
  const [incidents, setIncidents] = useState(dashboardData.incidents || 0);
  const [score] = useState(dashboardData.score || 0);

  // Read the live --app-primary-color CSS var (set by injectGlobalStyles),
  // falling back to platformSettings in localStorage.
  const accentVars = useMemo(() => {
    try {
      const cssColor = getComputedStyle(document.documentElement)
        .getPropertyValue('--app-primary-color').trim();
      if (cssColor) return buildAccentVars(cssColor);
      const settings = JSON.parse(localStorage.getItem('platformSettings') || '{}');
      return buildAccentVars(settings?.appearance?.primaryColor || '#1E3A5F');
    } catch {
      return buildAccentVars('#1E3A5F');
    }
  }, []);

  const displayName = user?.firstname
    ? `${user.firstname}${user.lastname ? ' ' + user.lastname : ''}`
    : user?.name || user?.email?.split('@')[0] || 'there';
  const courseCount = courses.count;
  const completedCourses  = courses.finished_count;
  const totalDocs    = documents.assigned;
  const completedDocs     = documents.finished;
  const pendingDocs       = documents.pending;
  const openIncidents     = incidents.count;
  const courseItems = courses.items || [];
  const DocumentItems = documents.items || [];
  const incidentItems = incidents.items || [];
  console.log({courseItems, DocumentItems, incidentItems});
  const refreshData = () => {
    setLoading(true);
    setTimeout(() => setLoading(false), 800);
  };

  return (
    <div className="eu-portal" style={accentVars}>

      {/* ── Hero Banner ───────────────────────────────────────── */}
      <motion.div
        className="eu-hero eu-animate-1"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
      >
        <div className="eu-hero-content">
          <div className="eu-hero-left">
            <p className="eu-greeting">{getGreeting()}</p>
            <h1 className="eu-hero-name">{displayName} 👋</h1>
            <p className="eu-hero-sub">
              Your personal Security & Awareness Portal &nbsp;·&nbsp;
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
            </p>
          </div>

          <div className="eu-hero-right">
            <ScoreRing score={score} />
            <div className="eu-score-info">
              <p className="eu-score-info-title">Security Awareness</p>
              <div className="eu-score-breakdown">
                <div className="eu-score-item">
                  <div className="eu-score-dot" style={{ background: '#34d399' }} />
                  LMS Progress &nbsp;·&nbsp; {completedCourses}/{courseCount} courses
                </div>
                <div className="eu-score-item">
                  <div className="eu-score-dot" style={{ background: '#fbbf24' }} />
                  Docs Acknowledged &nbsp;·&nbsp; {completedDocs}/{totalDocs}
                </div>
                <div className="eu-score-item">
                  <div className="eu-score-dot" style={{ background: '#f87171' }} />
                  Open Incidents &nbsp;·&nbsp; {openIncidents}
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* ── Stats Row ─────────────────────────────────────────── */}
      <motion.div
        className="eu-stats-row eu-animate-2"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <div className="eu-stat-card" onClick={() => navigate('/ai-lms')}>
          <div className="eu-stat-icon purple"><AcademicCapIcon /></div>
          <div className="eu-stat-text">
            <div className="eu-stat-value">{completedCourses}/{courseCount}</div>
            <div className="eu-stat-label">Courses Done</div>
          </div>
        </div>
        <div className="eu-stat-card" onClick={() => navigate('/documents')}>
          <div className="eu-stat-icon amber"><DocumentTextIcon /></div>
          <div className="eu-stat-text">
            <div className="eu-stat-value">{pendingDocs}</div>
            <div className="eu-stat-label">Docs Pending</div>
          </div>
        </div>
        <div className="eu-stat-card" onClick={() => navigate('/incident-reporting')}>
          <div className="eu-stat-icon red"><ExclamationTriangleIcon /></div>
          <div className="eu-stat-text">
            <div className="eu-stat-value">{openIncidents}</div>
            <div className="eu-stat-label">My Incidents</div>
          </div>
        </div>
        <div className="eu-stat-card" onClick={() => navigate('/risk-management')}>
          <div className="eu-stat-icon blue"><ShieldCheckIcon /></div>
          <div className="eu-stat-text">
            <div className="eu-stat-value">{score}%</div>
            <div className="eu-stat-label">Security Score</div>
          </div>
        </div>
      </motion.div>

      {/* ── Row 1: Quick Actions + Learning Progress ──────────── */}
      <div className="eu-grid">
        {/* Quick Actions */}
        <motion.div
          className="eu-card eu-animate-3"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.18 }}
        >
          <div className="eu-card-header">
            <div className="eu-card-title">
              <div className="eu-card-title-icon"><BellAlertIcon /></div>
              Quick Actions
            </div>
          </div>
          <div className="eu-quick-actions">
            <div className="eu-quick-action" onClick={() => navigate('/incident-reporting')}>
              <div className="eu-quick-action-icon" style={{ background: '#fee2e2', color: '#ef4444' }}>
                <ExclamationTriangleIcon style={{ width: 18, height: 18 }} />
              </div>
              <span className="eu-quick-action-label">Report an Incident</span>
              <span className="eu-quick-action-desc">Log a security event or concern</span>
            </div>
            <div className="eu-quick-action" onClick={() => navigate('/documents')}>
              <div className="eu-quick-action-icon" style={{ background: '#fef3c7', color: '#f59e0b' }}>
                <DocumentTextIcon style={{ width: 18, height: 18 }} />
              </div>
              <span className="eu-quick-action-label">Review Documents</span>
              <span className="eu-quick-action-desc">Acknowledge policy documents</span>
            </div>
            <div className="eu-quick-action" onClick={() => navigate('/ai-lms')}>
              <div className="eu-quick-action-icon" style={{ background: 'var(--eu-accent-light)', color: 'var(--eu-accent)' }}>
                <AcademicCapIcon style={{ width: 18, height: 18 }} />
              </div>
              <span className="eu-quick-action-label">Continue Learning</span>
              <span className="eu-quick-action-desc">Resume your security courses</span>
            </div>
            <div className="eu-quick-action" onClick={() => navigate('/risk-management')}>
              <div className="eu-quick-action-icon" style={{ background: '#d1fae5', color: '#10b981' }}>
                <ShieldCheckIcon style={{ width: 18, height: 18 }} />
              </div>
              <span className="eu-quick-action-label">View Risk Score</span>
              <span className="eu-quick-action-desc">Check your compliance status</span>
            </div>
          </div>
        </motion.div>

        {/* Learning Progress */}
        <motion.div
          className="eu-card eu-animate-4"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.25 }}
        >
          <div className="eu-card-header">
            <div className="eu-card-title">
              <div className="eu-card-title-icon"><AcademicCapIcon /></div>
              My Courses
            </div>
            <button className="eu-card-action" onClick={() => navigate('/ai-lms')}>
              View All <ArrowRightIcon style={{ width: 12, height: 12, display: 'inline', marginLeft: 4 }} />
            </button>
          </div>
          <div className="eu-course-list">
            {courseItems.map(course => (
              <div key={course.id} className="eu-course-item" onClick={() => navigate('/ai-lms')}>
                <div className="eu-course-thumb" style={{ background: 'var(--eu-accent-light)' }}>
                  {course.emoji}
                </div>
                <div className="eu-course-info">
                  <div className="eu-course-name">{course.module_title}</div>
                  <div className="eu-course-meta">⏱ {course.duration}</div>
                </div>
                <div className="eu-progress-wrap">
                  <div className="eu-progress-bar">
                    <div className="eu-progress-fill" style={{ width: `${course.progress}%` }} />
                  </div>
                  <div className="eu-progress-pct">{course.progress}%</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* ── Row 2: Documents + My Incidents ──────────────────── */}
      <div className="eu-grid">
        {/* Documents to Acknowledge */}
        <motion.div
          className="eu-card eu-animate-5"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.32 }}
        >
          <div className="eu-card-header">
            <div className="eu-card-title">
              <div className="eu-card-title-icon"><BookOpenIcon /></div>
              My Documents
            </div>
            <button className="eu-card-action" onClick={() => navigate('/documents')}>
              View All <ArrowRightIcon style={{ width: 12, height: 12, display: 'inline', marginLeft: 4 }} />
            </button>
          </div>
          <div className="eu-doc-list">
            {DocumentItems.map(doc => (
              <div key={doc.id} className="eu-doc-item" onClick={() => navigate('/documents')}>
                <div className="eu-doc-icon" style={{ background: 'var(--eu-accent-light)', color: 'var(--eu-accent)' }}>
                  <DocumentTextIcon />
                </div>
                <div className="eu-doc-info">
                  <div className="eu-doc-name">{doc.name}</div>
                  <div className="eu-doc-meta">Updated {formatTime(doc.updated)}</div>
                </div>
                <span className={`eu-doc-badge ${doc.status}`}>
                  {doc.status === 'acknowledged' ? '✓ Done' : doc.status === 'overdue' ? '⚠ Overdue' : 'Pending'}
                </span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* My Incidents */}
        <motion.div
          className="eu-card eu-animate-5"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.36 }}
        >
          <div className="eu-card-header">
            <div className="eu-card-title">
              <div className="eu-card-title-icon"><ClipboardDocumentListIcon /></div>
              My Incidents
            </div>
            <button className="eu-card-action" onClick={() => navigate('/incident-reporting')}>
              Report New
            </button>
          </div>
          {incidentItems.length === 0 ? (
            <div className="eu-empty">
              <CheckCircleIcon />
              <span className="eu-empty-text">No incidents to display</span>
            </div>
          ) : (
            <div className="eu-incident-list">
              {incidentItems.map(inc => (
                <div key={inc.id} className={`eu-incident-item ${inc.severity}`} onClick={() => navigate('/incident-reporting')}>
                  <div className={`eu-incident-dot ${inc.severity}`} />
                  <div className="eu-incident-info">
                    <div className="eu-incident-title">{inc.title}</div>
                    <div className="eu-incident-meta">{formatTime(inc.date)} &nbsp;·&nbsp; {inc.severity.toUpperCase()}</div>
                  </div>
                  <span className={`eu-incident-status ${inc.status}`}>
                    {inc.status.replace('_', ' ')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      </div>

      {/* ── Row 3: Security Tips + Activity ──────────────────── */}
      <div className="eu-grid">
        {/* Security Tips */}
        <motion.div
          className="eu-card eu-animate-6"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.42 }}
        >
          <div className="eu-card-header">
            <div className="eu-card-title">
              <div className="eu-card-title-icon"><LightBulbIcon /></div>
              Security Tips
            </div>
          </div>
          <div className="eu-tip-list">
            {SECURITY_TIPS.map((tip, i) => (
              <div key={i} className="eu-tip-item">
                <div className="eu-tip-icon-wrap" style={{ background: tip.bg, color: tip.color }}>
                  {tip.icon}
                </div>
                <div className="eu-tip-text">
                  <div className="eu-tip-title">{tip.title}</div>
                  <p className="eu-tip-desc">{tip.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Recent Activity */}
        <motion.div
          className="eu-card eu-animate-7"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.48 }}
        >
          <div className="eu-card-header">
            <div className="eu-card-title">
              <div className="eu-card-title-icon"><ClockIcon /></div>
              Recent Activity
            </div>
            <button className="eu-card-action" onClick={refreshData} disabled={loading}>
              {loading ? (
                <ArrowPathIcon style={{ width: 14, height: 14, display: 'inline', animation: 'spin 1s linear infinite' }} />
              ) : 'Refresh'}
            </button>
          </div>
          <div className="eu-activity-list">
            {MOCK_ACTIVITY.map(item => (
              <div key={item.id} className="eu-activity-item">
                <div className={`eu-activity-dot ${item.color}`} />
                <div className="eu-activity-content">
                  <div className="eu-activity-text">{item.text}</div>
                  <div className="eu-activity-time">{item.time}</div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

    </div>
  );
};

export default EndUserPortal;
